<script>
  import { beforeUpdate, getContext } from 'svelte';
  import { format } from 'svelte-i18n';
  import { link, useNavigate, useParams } from 'svelte-navigator';
  import { Breadcrumb, BreadcrumbItem, Button, Col, Container, Row, TabContent, TabPane, Nav, NavItem, NavLink } from '@sveltestrap/sveltestrap';
  import { createQuery } from '@tanstack/svelte-query';

  import { DeleteWarning } from '@client/components/delete-warning';
  import { Loader } from '@client/components/loader';
  import { getNotificationsContext } from '@client/components/notifications';

  import { PluginsService } from '@client/services/plugins';

  import { PLUGIN_BADGES } from '@client/enums';

  import { deepCopyArray } from '@common/utils/arrays';

  import { Diagnostics } from './diagnostics';
  import { Integration } from './integration';

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const { basepath } = getContext('config');

  const navigate = useNavigate();

  const params = useParams();

  let recipientEmail = '';

  let updating = false;

  let loadingDiagnostics = false;

  let diagnostics = null;
  let plugin = null;
  let rulename = '';

  let badges = deepCopyArray(PLUGIN_BADGES);
  let badgeConfig = {};

  const pluginQuery = createQuery({
    refetchOnWindowFocus: false,
    queryKey: ['edit-integration'],
    queryFn: async () => {
      const meta = await PluginsService.getById($params.id);

      if (!meta) {
        navigate('/manage/integrations');
        return;
      }

      return meta;
    }
  });

  const diagnosticsQueryFn = async () => {
    loadingDiagnostics = true;
    const response = await PluginsService.getDiagnostics(plugin._id, recipientEmail);
    diagnostics = response;

    loadingDiagnostics = false;

    return response;
  };

  const rulenameQuery = createQuery({
    refetchOnWindowFocus: false,
    queryKey: ['rulenames'],
    queryFn: async () => {
      const response = await PluginsService.getAll();
      let currentRulenames = [];

      response.forEach((plugin) => {
        if (plugin.rulename === $params.id) return;

        currentRulenames.push(plugin.rulename);
      });

      return currentRulenames;
    }
  });

  const onTab = async (evt) => {
    if (evt.detail === "diagnostics-tab") {
      await toggleDiagnosticsPanel();
    }
  }

  const onUpdate = async () => {
    updating = true;

    const response = await PluginsService.savePlugin(plugin);

    updating = false;

    if (response?.status >= 400) {
      notify($format('label.FAILURE.UPDATE', { values: { rulename: plugin.rulename } }), 'error');

      return;
    }

    refreshDiagnostics();

    notify($format('label.SUCCESS.UPDATE', { values: { item: plugin.rulename } }));
  };

  const confirmDeletion = async () => {
    updating = true;

    const response = await PluginsService.deletePlugin(plugin);

    updating = false;

    if (response?.status >= 400) {
      notify($format('label.FAILURE.DELETE', { values: { type: plugin.rulename } }), 'error');

      return;
    }

    notify($format('label.SUCCESS.DELETE', { values: { type: plugin.rulename } }));

    navigate('/manage/integrations');
  };

  let initialized = false;

  const toggleDiagnosticsPanel = async () => {
    if (!initialized) {
      await diagnosticsQueryFn();
      initialized = true;
    }
  };

  const refreshDiagnostics = () => diagnosticsQueryFn();

  const getBadgeConfig = (plugin) => {
    return badges[plugin?.provider] || null;
  };

  beforeUpdate(() => {
    badgeConfig = getBadgeConfig(plugin);
  });

  $: plugin = $pluginQuery.data;
  $: rulenames = $rulenameQuery.data;
  $: isDisabled = rulenames?.includes(plugin?.rulename) || plugin?.rulename.length < 3;
  $: invalidRulename = !plugin?.rulename || isDisabled;
  $: invalidFeedback = rulenames?.includes(plugin?.rulename)
    ? $format('label.VALIDATION_NAME_EXISTS', {
        values: { type: $format('label.INTEGRATION'), param: $format('label.NAME').toLowerCase() }
      })
    : $format('label.VALIDATION_TOO_SHORT', { values: { param: $format('label.NAME'), length: 3 } });
</script>

<div class="page">
  <Loader backdrop show={$pluginQuery.isLoading || $pluginQuery.isFetching || updating} />
  <div class="page-header">
    <Container fluid>
      <Row>
        <Col md="12">
          <Breadcrumb>
            <BreadcrumbItem>
              <a href="{basepath}/manage" use:link>{$format('label.MANAGE')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <a href="{basepath}/manage/integrations" use:link>{$format('label.INTEGRATIONS')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem active>{$format('label.EDIT_INTEGRATION')}</BreadcrumbItem>
          </Breadcrumb>
        </Col>
      </Row>
      <Row class="flex-align--center">
        <Col xs="9" sm="6" md="6">
          <div class="header">
            <h1 class="page-title">{plugin?.rulename || ''}</h1>
            <div
              class="badge"
              style="background-color: {badgeConfig?.color}; border: 1px solid {badgeConfig?.color}; color: {badgeConfig?.textColor}"
            >
              {$format(badgeConfig?.label)}
            </div>
          </div>
        </Col>
        <Col xs="3" sm="6" md="6" class="text-right">
          <Button color="secondary" class="hide--mobile me-2" on:click={() => navigate('/manage/integrations')}>
            {$format('label.CANCEL')}
          </Button>
          <Button color="primary" on:click={onUpdate} disabled={isDisabled}>{$format('label.UPDATE')}</Button>
        </Col>
      </Row>
    </Container>
  </div>

  {#if !$pluginQuery.isFetching}
    <div id="edit-integration" class="page-content">
      <div class="page-width">
        <Container fluid>
          <Row class="pt-3">
            <TabContent on:tab={onTab}>
              <TabPane tabId="integration-tab" id="integration-tab" tab={$format('label.INTEGRATION')} active>
                <Integration bind:plugin bind:invalidRulename bind:isDisabled bind:invalidFeedback />
              </TabPane>
              <TabPane tabId="diagnostics-tab" id="diagnostics-tab" tab={$format('label.DIAGNOSTICS')}>
                <Diagnostics bind:recipientEmail {plugin} {loadingDiagnostics} {diagnostics} {refreshDiagnostics} />
              </TabPane>
            </TabContent>
            <DeleteWarning
              rulename={plugin.rulename}
              objectType="label.INTEGRATION"
              deleteRequirementType="label.INTEGRATION"
              deleteRequirementParam="label.NAME"
              onDelete={confirmDeletion}
            />
          </Row>
        </Container>
      </div>
    </div>
  {/if}
</div>

<style lang="scss" src="./edit.scss"></style>
